<script setup lang="ts">
import { UiChip, UiIcon, UiText } from "@shopexperts/shared";
import { quoteCopy } from "@/v2Projects/utils/quoteCopy";
import type {
  QuoteMessageCardMetric,
  QuoteMessageCardTimelineStep,
} from "./QuoteMessageCard.types";
import QuoteMessageCardActions from "./QuoteMessageCardActions.vue";
import QuoteMessageCardExpertOffering from "./QuoteMessageCardExpertOffering.vue";
import QuoteMessageCardInvoice from "./QuoteMessageCardInvoice.vue";
import QuoteMessageCardMetricGrid from "./QuoteMessageCardMetricGrid.vue";
import QuoteMessageCardTextBox from "./QuoteMessageCardTextBox.vue";
import QuoteMessageCardTimeline from "./QuoteMessageCardTimeline.vue";

defineProps<{
  open: boolean;
  statusLabel: string;
  statusBgClass: string;
  statusTextClass: string;
  senderName: string;
  senderAvatar?: string;
  senderInitials?: string;
  expertRole?: string | null;
  metricCards: QuoteMessageCardMetric[];
  scope?: string | null;
  scopeIsHtml: boolean;
  expirationTerms?: string | null;
  timelineSteps: QuoteMessageCardTimelineStep[];
  isPaidQuote: boolean;
  invoiceUrl?: string | null;
  showAcceptQuote: boolean;
  showRejectQuote: boolean;
}>();

defineEmits<{
  close: [];
  reject: [];
  accept: [];
  openInvoice: [];
  copyInvoiceSuccess: [value: string];
}>();
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-90 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-10 max-sm:p-0 max-sm:items-stretch max-sm:overflow-hidden"
      role="dialog"
      aria-modal="true"
      :aria-label="quoteCopy.proposalDetails.title"
      @click.self="$emit('close')"
    >
      <div
        class="relative w-full max-w-200 overflow-hidden rounded-2xl bg-white shadow-2xl p-6 md:p-8 flex flex-col gap-8 max-sm:rounded-none max-sm:shadow-none max-sm:p-0 max-sm:gap-0 max-sm:h-full max-sm:max-h-screen"
      >
        <div
          class="flex items-start justify-between gap-6 max-sm:sticky max-sm:top-0 max-sm:z-10 max-sm:bg-white max-sm:px-5 max-sm:pt-4 max-sm:pb-3 max-sm:border-b max-sm:border-se-gray-200"
        >
          <div class="flex flex-col gap-2 max-sm:gap-1.5 max-sm:min-w-0">
            <div class="md:hidden">
              <UiChip
                size="xs"
                variant="plain"
                :text="statusLabel"
                :bg-color-class="statusBgClass"
                :text-color-class="statusTextClass"
                class="self-start"
              />
            </div>
            <div class="hidden md:block">
              <UiChip
                size="tag"
                variant="plain"
                :text="statusLabel"
                :bg-color-class="statusBgClass"
                :text-color-class="statusTextClass"
                class="hidden"
              />
            </div>
            <UiText as="h3" class="text-se-dark">
              {{ quoteCopy.proposalDetails.title }}
            </UiText>
          </div>

          <button
            type="button"
            class="absolute right-0 top-0 z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-bl-lg rounded-tr-2xl bg-se-gray-300 hover:bg-se-gray-200 text-se-gray-700 cursor-pointer transition-all duration-500 group/close-button max-sm:static max-sm:h-9 max-sm:w-9 max-sm:rounded-full! max-sm:bg-se-gray-100 max-sm:hover:bg-se-gray-200 max-sm:-mr-1"
            :aria-label="quoteCopy.proposalDetails.closeLabel"
            @click="$emit('close')"
          >
            <UiIcon
              name="close"
              class="h-6 w-6 group-hover/close-button:scale-105 transition-all duration-100 max-sm:h-5 max-sm:w-5"
            />
          </button>
        </div>

        <div
          class="flex flex-col gap-8 max-sm:gap-6 max-sm:flex-1 max-sm:min-h-0 max-sm:overflow-y-auto max-sm:px-5 max-sm:py-5"
        >
          <QuoteMessageCardExpertOffering
            :sender-name="senderName"
            :sender-avatar="senderAvatar"
            :sender-initials="senderInitials"
            :expert-role="expertRole"
          />

          <QuoteMessageCardMetricGrid :cards="metricCards" />

          <QuoteMessageCardTextBox
            :title="quoteCopy.proposalDetails.scopeTitle"
            :content="scope"
            :html="scopeIsHtml"
            :fallback="quoteCopy.proposalDetails.noScopeFallback"
          />

          <QuoteMessageCardTextBox
            v-if="expirationTerms"
            :title="quoteCopy.hoursBankExpirationTermsDisplayLabel"
            :content="expirationTerms"
          />

          <QuoteMessageCardTimeline :steps="timelineSteps" />

          <QuoteMessageCardInvoice
            v-if="isPaidQuote"
            :invoice-url="invoiceUrl"
            @copy-success="$emit('copyInvoiceSuccess', $event)"
          />
        </div>

        <QuoteMessageCardActions
          :is-paid-quote="isPaidQuote"
          :invoice-url="invoiceUrl"
          :show-accept-quote="showAcceptQuote"
          :show-reject-quote="showRejectQuote"
          class="max-sm:sticky max-sm:bottom-0 max-sm:z-10 max-sm:bg-white max-sm:px-5 max-sm:py-4 max-sm:border-t max-sm:border-se-gray-200"
          @reject="$emit('reject')"
          @accept="$emit('accept')"
          @open-invoice="$emit('openInvoice')"
        />
      </div>
    </div>
  </Teleport>
</template>
